import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import Drawer from 'relements/build/esm/overlays/drawer';
import Icon from 'relements/build/esm/ui/icon';
import Tabs from 'relements/build/esm/ui/tabs';
import useLoader from 'relements/build/esm/hooks/useLoader';

import { createAlert } from '@src/actions/BotActions';
import { AlertTypes } from '@src/constants/MogamboConstants';
import { GOOGLE_ANALYTICS, PERMISSION_LEVELS } from '@src/const';
import BotStore from '@src/store';

import {
  userHasPermission,
  permissionErrorAlert,
  sendGoogleAnalyticsEvents,
} from '@src/actions/_utils';

import * as API from './api/api';
import DebugInfo from './components/DebugInfo';
import DebugDetails from './components/DebugDetails';
import styles from './DebugWindow.scss';

const DEBUG_TABS = [
  {
    title: 'Basic Info',
    path: 'basic',
  },
  {
    title: 'Detailed Info',
    path: 'detailed',
  },
];

/**
 * This function renders the debug window for testbot in mogambo
 * @param {Object} prop - Object containing all the props
 * @param {boolean} prop.active - decides whether drawer is active or not
 * @param {string} prop.messageId - id of the message whose debug log to fetch
 * @param {Function} prop.onClose - function to be called on closing the drawer
 */
function DebugWindow({ active, messageId, onClose }) {
  const { renderLoader, activateLoader, deactivateLoader } = useLoader(true);
  const hasSelfServeLayout = BotStore.getSelfServeLayout();

  // default tab is info
  const [selectedTab, selectTab] = useState('basic');
  const [debugLogs, setDebugLogs] = useState();

  // function for changing the tab in debug window
  const changeTab = React.useCallback((tab) => {
    selectTab(tab);

    sendGoogleAnalyticsEvents(
      GOOGLE_ANALYTICS.CATEGORY.DEBUG,
      GOOGLE_ANALYTICS.EVENTS.CHANGE_DEBUG_TAB,
      tab,
      GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
    );
  }, []);

  // makes an api call, whenever messageId is changed
  useEffect(() => {
    if (messageId) {
      if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_VIEW)) {
        permissionErrorAlert('view debug logs');
        deactivateLoader();
        return;
      }

      activateLoader();
      API.fetchDebugLogs(messageId)
        .then((res) => {
          setDebugLogs(res.body);
          deactivateLoader();
        })
        .catch((err) => {
          createAlert(err, null, AlertTypes.NEGATIVE);
          setDebugLogs();
          deactivateLoader();
        });

      sendGoogleAnalyticsEvents(
        GOOGLE_ANALYTICS.CATEGORY.DEBUG,
        GOOGLE_ANALYTICS.EVENTS.VIEW_DEBUG_LOGS,
        GOOGLE_ANALYTICS.LABEL.DEBUG_LOGS_OPEN,
        GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
      );
    }
  }, [messageId]);

  return (
    <Drawer
      active={active}
      position="LEFT"
      width={360}
      offset={360}
      zIndex={hasSelfServeLayout ? 3 : 2}
      noOverlay
      noDisable
      prefixClassName={`debugDrawer${
        hasSelfServeLayout ? '_ww' : ''
      }`}
      attachTo="app-body"
    >
      {renderLoader()}
      <div
        className={`${styles.debugHeader} ${
          hasSelfServeLayout ? styles.debugHeader_ww : ''
        }`}
      >
        <div className={styles.debugHeaderTitle}>Message Log</div>
        <Icon src="closeV2" onClick={onClose} />
      </div>
      <Tabs value={selectedTab} className={styles.debugHorizontalMenu}>
        {DEBUG_TABS.map((item) => (
          <Tabs.Item
            onClick={changeTab}
            prefixClassName={styles.debugHorizontalMenu}
            key={item.path}
            value={item.path}
          >
            {item.title}
          </Tabs.Item>
        ))}
      </Tabs>
      <div style={{ overflow: 'hidden', height: '100%' }}>
        {selectedTab === 'basic' ? (
          <DebugInfo debugData={debugLogs} />
        ) : (
          <DebugDetails debugData={debugLogs} />
        )}
      </div>
    </Drawer>
  );
}

DebugWindow.propTypes = {
  active: PropTypes.bool,
  onClose: PropTypes.func,
  messageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

DebugWindow.defaultProps = {
  active: false,
  onClose: () => {},
  messageId: '',
};

export default DebugWindow;
